/**
 * CheTuo Web — Order detail page (工单详情).
 *
 * Shows full order info: items, customer, vehicle, status timeline,
 * and allows status transitions and payment confirmation.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { getOrderDetail, updateOrderStatus } from '../../api/orders';
import { getPaymentByOrder, confirmPayment } from '../../api/payments';
import { ApiError } from '../../api/client';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  open: '待处理',
  in_progress: '施工中',
  done: '待收款',
  paid: '已收款',
  closed: '已关闭',
  cancelled: '已取消',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  done: 'bg-purple-100 text-purple-700',
  paid: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
  cancelled: 'bg-red-100 text-red-700',
};

const NEXT_STATUS_LABELS: Record<string, string> = {
  open: '开始施工',
  in_progress: '完成施工',
  done: '确认收款',
  cancelled: '取消工单',
};

const PAYMENT_METHODS = [
  { value: 'cash', label: '现金' },
  { value: 'wechat', label: '微信' },
  { value: 'alipay', label: '支付宝' },
  { value: 'card', label: '银行卡' },
  { value: 'transfer', label: '转账' },
];

export function OrderDetailPage(): React.JSX.Element {
  const { orderId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrderDetail(orderId),
    enabled: !!orderId,
  });

  const { data: payment } = useQuery({
    queryKey: ['payment', orderId],
    queryFn: () => getPaymentByOrder(orderId),
    enabled: !!orderId,
  });

  const [showPayModal, setShowPayModal] = useState(false);
  const [payMethod, setPayMethod] = useState('cash');
  const [error, setError] = useState('');

  const statusMutation = useMutation({
    mutationFn: (status: string) => updateOrderStatus(orderId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '状态更新失败');
    },
  });

  const payMutation = useMutation({
    mutationFn: () =>
      confirmPayment({
        orderId,
        method: payMethod,
        amount: order?.totalAmount ?? '0',
        idempotencyKey: `pay_${orderId}_${Date.now()}`,
      }),
    onSuccess: () => {
      setShowPayModal(false);
      queryClient.invalidateQueries({ queryKey: ['payment', orderId] });
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '收款确认失败');
    },
  });

  if (isLoading) {
    return <div className="py-12 text-center text-gray-500">加载中...</div>;
  }

  if (!order) {
    return <div className="py-12 text-center text-gray-500">工单不存在</div>;
  }

  const handleStatusChange = (status: string) => {
    setError('');
    statusMutation.mutate(status);
  };

  const handlePay = () => {
    setError('');
    payMutation.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Back button + title */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/shop-admin/orders')}
          className="text-gray-500 hover:text-gray-700"
        >
          ← 返回列表
        </button>
        <h1 className="text-2xl font-bold text-gray-900">工单详情</h1>
        <span
          className={`rounded px-3 py-1 text-sm font-medium ${STATUS_COLORS[order.status] ?? 'bg-gray-100 text-gray-700'}`}
        >
          {STATUS_LABELS[order.status] ?? order.status}
        </span>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">{error}</div>
      )}

      {/* Vehicle + Customer info */}
      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>车辆信息</CardTitle>
          </CardHeader>
          <CardContent>
            {order.vehicle ? (
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">车牌</dt>
                  <dd className="font-medium text-gray-900">{order.vehicle.plate}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">品牌</dt>
                  <dd className="text-gray-900">{order.vehicle.brand ?? '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">车型</dt>
                  <dd className="text-gray-900">{order.vehicle.model ?? '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">年份</dt>
                  <dd className="text-gray-900">{order.vehicle.year ?? '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">VIN</dt>
                  <dd className="text-gray-900">{order.vehicle.vin ?? '—'}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-gray-500">车辆信息缺失</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>客户信息</CardTitle>
          </CardHeader>
          <CardContent>
            {order.customer ? (
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">姓名</dt>
                  <dd className="font-medium text-gray-900">{order.customer.name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">手机号</dt>
                  <dd className="text-gray-900">{order.customer.phone ?? '—'}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-gray-500">客户信息缺失</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Order items */}
      <Card>
        <CardHeader>
          <CardTitle>维修项目 ({order.items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-600">
                <th className="pb-3 pr-4 font-medium">项目名称</th>
                <th className="pb-3 pr-4 font-medium">数量</th>
                <th className="pb-3 pr-4 font-medium">单价</th>
                <th className="pb-3 pr-4 font-medium">金额</th>
                <th className="pb-3 pr-4 font-medium">备注</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item) => (
                <tr key={item.id} className="border-b border-gray-100">
                  <td className="py-3 pr-4 font-medium text-gray-900">{item.name}</td>
                  <td className="py-3 pr-4 text-gray-700">{item.quantity}</td>
                  <td className="py-3 pr-4 text-gray-700">¥{item.unitPrice}</td>
                  <td className="py-3 pr-4 font-medium text-gray-900">¥{item.amount}</td>
                  <td className="py-3 pr-4 text-gray-500">{item.remark ?? '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200">
                <td colSpan={3} className="pt-3 text-right text-gray-600">合计</td>
                <td className="pt-3 pr-4 text-lg font-bold text-gray-900">¥{order.totalAmount}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {/* Remark */}
      {order.remark && (
        <Card>
          <CardHeader>
            <CardTitle>工单备注</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700">{order.remark}</p>
          </CardContent>
        </Card>
      )}

      {/* Payment info */}
      {payment && (
        <Card>
          <CardHeader>
            <CardTitle>收款记录</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">收款方式</dt>
                <dd className="font-medium text-gray-900">
                  {PAYMENT_METHODS.find((m) => m.value === payment.method)?.label ?? payment.method}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">收款金额</dt>
                <dd className="font-medium text-gray-900">¥{payment.amount}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">收款时间</dt>
                <dd className="text-gray-900">
                  {new Date(payment.confirmedAt).toLocaleString('zh-CN')}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}

      {/* Actions: status transitions */}
      <Card>
        <CardHeader>
          <CardTitle>操作</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {order.allowedNextStatuses.map((status) => {
              if (status === 'paid') {
                return (
                  <Button
                    key={status}
                    onClick={() => setShowPayModal(true)}
                    disabled={statusMutation.isPending}
                  >
                    {NEXT_STATUS_LABELS[status] ?? status}
                  </Button>
                );
              }
              return (
                <Button
                  key={status}
                  variant={status === 'cancelled' ? 'danger' : 'default'}
                  onClick={() => handleStatusChange(status)}
                  disabled={statusMutation.isPending}
                >
                  {NEXT_STATUS_LABELS[status] ?? status}
                </Button>
              );
            })}
            {order.allowedNextStatuses.length === 0 && (
              <p className="text-sm text-gray-500">当前状态无可用操作</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment modal */}
      {showPayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>确认收款</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">收款金额</p>
                  <p className="text-2xl font-bold text-gray-900">¥{order.totalAmount}</p>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">收款方式</label>
                  <select
                    value={payMethod}
                    onChange={(e) => setPayMethod(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowPayModal(false)} className="flex-1">
                    取消
                  </Button>
                  <Button onClick={handlePay} disabled={payMutation.isPending} className="flex-1">
                    {payMutation.isPending ? '确认中...' : '确认收款'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
